import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, CheckCircle, AlertCircle, User, Linkedin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SpeakerUploadPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [speakerInfo, setSpeakerInfo] = useState(null);
  const [speakerName, setSpeakerName] = useState('');
  const [speakerTitle, setSpeakerTitle] = useState('');
  const [bio, setBio] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [photo, setPhoto] = useState(null);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/speaker-upload/validate/${token}`);
      
      if (response.ok) {
        const data = await response.json();
        setValid(true);
        setSpeakerInfo(data);
        // Pre-fill existing data if available
        if (data.existing_name) setSpeakerName(data.existing_name);
        if (data.existing_title) setSpeakerTitle(data.existing_title);
        if (data.existing_bio) setBio(data.existing_bio);
        if (data.existing_linkedin_url) setLinkedinUrl(data.existing_linkedin_url);
      } else {
        setValid(false);
        setError('This upload link is invalid or has expired.');
      }
    } catch (err) {
      console.error('Validation error:', err);
      setValid(false);
      setError('Failed to validate upload link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('speaker_name', speakerName);
      formData.append('speaker_title', speakerTitle);
      formData.append('bio', bio);
      formData.append('linkedin_url', linkedinUrl);
      if (photo) formData.append('photo', photo);

      const response = await fetch(`${BACKEND_URL}/api/speaker-upload/${token}`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        alert(data.detail || 'Upload failed. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="text-[#A2FF00] text-xl">Validating upload link...</div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#0A0A0A] border border-red-500/50 rounded-sm p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Invalid Link
          </h2>
          <p className="text-gray-400">{error}</p>
          <p className="text-gray-500 text-sm mt-4">
            Please contact the event organizer for a new upload link.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#0A0A0A] border border-[#A2FF00] rounded-sm p-8 text-center">
          <CheckCircle className="w-16 h-16 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Upload Successful!
          </h2>
          <p className="text-gray-400">Your speaker information has been submitted.</p>
          <p className="text-gray-500 text-sm mt-4">
            Thank you for providing your details for the QSECDEF event.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-[#0A0A0A] border border-white/10 rounded-sm p-8">
        <div className="text-center mb-8">
          <User className="w-12 h-12 text-[#A2FF00] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Speaker Upload Portal
          </h2>
          <p className="text-gray-400">
            Welcome{speakerInfo?.speaker_name ? `, ${speakerInfo.speaker_name}` : ''}! Please provide your bio and photo for the upcoming QSECDEF event.
          </p>
          {speakerInfo?.company_name && (
            <p className="text-[#A2FF00] text-sm mt-2">
              Event: {speakerInfo.company_name}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="speaker-upload-form">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Speaker Name *
            </label>
            <p className="text-xs text-gray-500 mb-2">The name and spelling we will use on your presentation and materials</p>
            <input
              type="text"
              value={speakerName}
              onChange={(e) => setSpeakerName(e.target.value)}
              required
              data-testid="speaker-name-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Dr. Jane Smith"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Title *
            </label>
            <p className="text-xs text-gray-500 mb-2">Your title used on your presentation and materials</p>
            <input
              type="text"
              value={speakerTitle}
              onChange={(e) => setSpeakerTitle(e.target.value)}
              required
              data-testid="speaker-title-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Chief Security Officer, Company Inc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Bio (max 2000 characters) *
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={2000}
              rows={6}
              required
              data-testid="bio-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="Tell us about yourself, your experience, and what you'll be speaking about..."
            />
            <div className="text-xs text-gray-500 mt-1 text-right">
              {bio.length}/2000
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
              <Linkedin className="w-4 h-4" /> LinkedIn Profile URL
            </label>
            <input
              type="url"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              data-testid="linkedin-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] focus:ring-1 focus:ring-[#A2FF00] rounded-sm text-white p-3"
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Professional Photo (JPG/PNG) *
            </label>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setPhoto(e.target.files[0])}
              required={!speakerInfo?.existing_photo}
              data-testid="photo-input"
              className="w-full bg-[#111] border border-white/20 focus:border-[#A2FF00] rounded-sm text-white p-3 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[#A2FF00] file:text-black file:font-bold"
            />
            <p className="text-xs text-gray-500 mt-1">
              Please upload a high-quality professional headshot.
            </p>
            {speakerInfo?.existing_photo && (
              <p className="text-xs text-[#A2FF00] mt-1">
                You already have a photo uploaded. Upload a new one to replace it.
              </p>
            )}
          </div>

          <div className="bg-[#111] border border-white/10 rounded-sm p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                data-testid="consent-checkbox"
                className="w-5 h-5 mt-0.5 rounded border-white/20 bg-[#0A0A0A] text-[#A2FF00] focus:ring-[#A2FF00] focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-sm text-gray-300 leading-relaxed">
                I understand that my information will be used to promote the event, I understand video from the event will be used on the QSECDEF platform and social media accounts.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={uploading || !consent}
            data-testid="submit-button"
            className="w-full bg-[#A2FF00] text-black py-3 font-bold uppercase tracking-wider rounded-sm hover:bg-[#8EFF00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Submit Speaker Information'}
          </button>
        </form>

        <p className="text-center text-gray-500 text-xs mt-6">
          Powered by Quantum Security and Defence
        </p>
      </div>
    </div>
  );
};

export default SpeakerUploadPortal;
